import type { FirebaseApp } from 'firebase/app';
import {
  collection,
  doc,
  getDocs,
  limit as limiter,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
  type Firestore,
  type Query,
  type QueryConstraint,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable, type Functions } from 'firebase/functions';
import { deleteObject, getBlob, getBytes, listAll, ref, uploadBytes, type FirebaseStorage } from 'firebase/storage';
import type { EmulatorEndpoint } from '@taki/runtime-config';
import { noteCorrespond, termeDeRecherche, type CollabSynchro, type Note, type NoteCollection, type NoteTag } from '@taki/data-model';
// Les TYPES seulement : la brique (et Yjs avec elle) se charge a l'ouverture
// d'une note, par `import()`, pour ne pas entrer dans le bundle principal que
// toutes les orgs chargent, meme sans Smart Notes.
import type { OptionsCollab, SessionCollab } from './collab.js';

/**
 * LES NOTES (app Smart Notes), COTE CLIENT : lire, demander, et editer le
 * contenu a plusieurs.
 *
 * Le client LIT trois collections sous `orgs/{orgId}/` (`notes`, `notesTags`,
 * `notesCollections`), en temps reel, et il n'y ECRIT JAMAIS : tout geste
 * (creer, renommer, etiqueter, mettre a la corbeille...) passe par la callable
 * a operations `notesApi` (`functions/src/notes/`), la meme que le MCP et Lia.
 * Le CONTENU, lui, s'ecrit en direct : c'est la brique collaborative du socle
 * (`collab.ts`) sous la fiche de la note, et les pieces jointes vont dans
 * Storage (`orgs/{orgId}/notes/{noteId}/blobs/{cle}`).
 *
 * DEUX REQUETES PAR LISTE, ET C'EST LES RULES QUI L'IMPOSENT (comme Flux). Une
 * note se lit si elle est a l'org (`portee == 'org'`) OU si on en est l'auteur :
 * une requete sans l'un de ces filtres serait refusee en bloc. D'ou deux
 * requetes, chacune permise, fusionnees par id cote client.
 *
 * LES CONSTANTES CI-DESSOUS RECOPIENT `functions/src/notes/` et
 * `functions/src/runtime.ts` (`functions/` ne depend d'aucun `@taki/*`,
 * docs/FUNCTIONS.md) ; un test les compare quand les deux existent.
 */

/** DOIT valoir `FUNCTIONS_REGION` de `functions/src/runtime.ts`. */
export const NOTES_FUNCTIONS_REGION = 'europe-west1';
export const NOTES_CALLABLE = 'notesApi';
export const NOTES_COLLECTION = 'notes';
export const NOTES_TAGS_COLLECTION = 'notesTags';
export const NOTES_COLLECTIONS_COLLECTION = 'notesCollections';
/** Le nombre de resultats d'une recherche serveur, par defaut et au plus. */
export const NOTES_RECHERCHE_LIMITE_DEFAUT = 50;
export const NOTES_RECHERCHE_LIMITE_MAX = 200;
/** Plafond d'une piece jointe (les regles Storage disent la meme chose). */
export const NOTES_BLOB_MAX_OCTETS = 50 * 1024 * 1024;
/** Une cle de piece jointe : celle de BlockSuite (sha256 en base64url). */
export const NOTES_BLOB_CLE = /^[A-Za-z0-9_=.-]{1,200}$/;

/** Les operations de `notesApi` (recopiees cote serveur ; un test compare). */
export const NOTES_OPS = [
  'creer',
  'journal',
  'synchro',
  'renommer',
  'deplacer',
  'etiqueter',
  'favori',
  'corbeille',
  'restaurer',
  'supprimer',
  'dupliquer',
  'ajouterMarkdown',
  'lireMarkdown',
  'compacter',
  'tagCreer',
  'tagModifier',
  'tagSupprimer',
  'collectionCreer',
  'collectionModifier',
  'collectionSupprimer',
] as const;
export type NotesOp = (typeof NOTES_OPS)[number];

export interface FiltreListeNotes {
  /** `true` : la corbeille seulement ; defaut : hors corbeille. */
  corbeille?: boolean;
}

export interface BlobsDeNote {
  get(cle: string): Promise<Blob | null>;
  /** Rend la cle. */
  set(cle: string, blob: Blob): Promise<string>;
  delete(cle: string): Promise<void>;
  list(): Promise<string[]>;
}

export interface NotesClient {
  /** Deux requetes fusionnees (portee org, auteurUid moi), deletedAt == null ; tri modifieeAt desc. */
  observerListe(filtre: FiltreListeNotes, cb: (notes: Note[]) => void, erreur?: (e: unknown) => void): () => void;
  observer(noteId: string, cb: (note: Note | null) => void, erreur?: (e: unknown) => void): () => void;
  observerTags(cb: (tags: NoteTag[]) => void, erreur?: (e: unknown) => void): () => void;
  observerCollections(cb: (c: NoteCollection[]) => void, erreur?: (e: unknown) => void): () => void;
  /** Recherche serveur sur le premier mot (`recherche array-contains`), puis `noteCorrespond` pour les autres. */
  chercher(saisie: string, limite?: number): Promise<Note[]>;
  /** Une operation de `notesApi`. Leve une `NotesError` au message court du serveur. */
  appeler<T = unknown>(op: NotesOp, donnees?: Record<string, unknown>): Promise<T>;
  /** Le contenu Yjs de la note, en temps reel. */
  ouvrirContenu(noteId: string, options: Omit<OptionsCollab, 'chemin' | 'uid' | 'compacter' | 'lireStockage'>): SessionCollab;
  /** Ecrit une maj d'un coup (import d'un fichier dans une note creee `sansContenu`). */
  ecrireMaj(noteId: string, update: Uint8Array): Promise<void>;
  /** Pieces jointes de l'editeur, dans Storage. */
  blobs(noteId: string): BlobsDeNote;
}

/** Une erreur de `notesApi` (ou du client des notes), au message deja lisible. */
export class NotesError extends Error {
  override readonly name = 'NotesError';
  readonly code: string;
  constructor(message: string, code: string, options?: { cause?: unknown }) {
    super(message);
    this.code = code;
    if (options?.cause !== undefined) this.cause = options.cause;
    Object.setPrototypeOf(this, NotesError.prototype);
  }
}

/* -------------------------------------------------------------------------- */
/* Fonctions pures (testees dans tests/notes.test.mjs)                         */
/* -------------------------------------------------------------------------- */

function estTimestamp(valeur: unknown): valeur is { toDate(): Date } {
  return valeur !== null && typeof valeur === 'object' && typeof (valeur as { toDate?: unknown }).toDate === 'function';
}

/** Les Timestamp de la base deviennent des `Date`, a toute profondeur. */
function versDates<T = unknown>(valeur: unknown): T {
  if (estTimestamp(valeur)) return valeur.toDate() as T;
  if (valeur instanceof Date) return valeur as T;
  if (Array.isArray(valeur)) return valeur.map((v) => versDates(v)) as T;
  if (valeur !== null && typeof valeur === 'object') {
    const sortie: Record<string, unknown> = {};
    for (const [clef, v] of Object.entries(valeur as Record<string, unknown>)) sortie[clef] = versDates(v);
    return sortie as T;
  }
  return valeur as T;
}

/** Un document lu : ses dates converties, son id pose (l'id du document gagne). */
export function lireDocumentNote<T>(id: string, donnees: DocumentData): T {
  return { ...versDates<Record<string, unknown>>(donnees), id } as T;
}

function temps(date: unknown): number {
  return date instanceof Date ? date.getTime() : 0;
}

/** Les plus recemment modifiees d'abord (puis par id, pour un ordre stable). */
export function parModificationDeNote(a: Pick<Note, 'id' | 'modifieeAt'>, b: Pick<Note, 'id' | 'modifieeAt'>): number {
  return temps(b.modifieeAt) - temps(a.modifieeAt) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/** Par nom, a la francaise. */
export function parNom(a: { nom: string; id: string }, b: { nom: string; id: string }): number {
  return a.nom.localeCompare(b.nom, 'fr', { sensitivity: 'base' }) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/**
 * FUSIONNE LES RESULTATS DES DEUX REQUETES par id, sans doublon (une note org
 * de l'auteur arrive par les deux : c'est le meme document), puis trie. Une
 * liste absente (requete pas encore revenue) compte pour vide.
 */
export function fusionnerParIdNotes<T extends { id: string }>(listes: ReadonlyArray<readonly T[] | null>, comparer: (a: T, b: T) => number): T[] {
  const parId = new Map<string, T>();
  for (const liste of listes) for (const element of liste ?? []) parId.set(element.id, element);
  return [...parId.values()].sort(comparer);
}

/** Les notes qui correspondent a TOUS les mots saisis, les plus recentes d'abord, au plus `limite`. */
export function filtrerRechercheNotes(notes: readonly Note[], saisie: string, limite: number): Note[] {
  return notes.filter((n) => n.deletedAt == null && noteCorrespond(n, saisie)).sort(parModificationDeNote).slice(0, limite);
}

/** La limite d'une recherche, bornee. */
export function limiteDeRecherche(limite: number | undefined): number {
  const n = Math.floor(limite ?? NOTES_RECHERCHE_LIMITE_DEFAUT);
  return Number.isFinite(n) ? Math.max(1, Math.min(NOTES_RECHERCHE_LIMITE_MAX, n)) : NOTES_RECHERCHE_LIMITE_DEFAUT;
}

/** Le chemin Storage d'une piece jointe ; leve sur une cle qui n'a pas la forme de BlockSuite. */
export function cheminDeBlob(orgId: string, noteId: string, cle: string): string {
  if (!NOTES_BLOB_CLE.test(cle)) throw new NotesError('Pièce jointe invalide.', 'invalid-argument');
  return `orgs/${orgId}/notes/${noteId}/blobs/${cle}`;
}

/** Le message d'une erreur de callable, sans l'habillage de Firebase. */
export function messageDErreurNotes(erreur: unknown): NotesError {
  if (erreur instanceof NotesError) return erreur;
  const code = String((erreur as { code?: unknown } | null)?.code ?? 'inconnu').replace(/^(functions|firestore|storage)\//, '');
  const brut = erreur instanceof Error ? erreur.message : String(erreur);
  const message = brut.replace(/^Firebase: /, '').replace(/ \((functions|firestore|storage)\/[a-z-]+\)\.?$/, '').trim();
  if (code === 'unavailable' || (code === 'internal' && message === 'internal')) {
    return new NotesError('Service des notes injoignable.', code, { cause: erreur });
  }
  if (code === 'unauthenticated') return new NotesError('Connexion requise.', code, { cause: erreur });
  if (code === 'permission-denied' && /missing or insufficient permissions/i.test(message)) {
    return new NotesError('Accès refusé.', code, { cause: erreur });
  }
  return new NotesError(message === '' ? 'Échec.' : message, code, { cause: erreur });
}

/**
 * UNE SESSION DONT LA BRIQUE SE CHARGE EN DIFFERE : `ouvrirContenu` rend tout
 * de suite (le contrat est synchrone), la session reelle s'ouvre quand le
 * module est la. Fermee avant, elle ne s'ouvre jamais. L'editeur attend `pret`
 * avant d'ecrire : une modification anterieure ne serait pas ecoutee.
 */
export function sessionDifferee(
  ouvreur: Promise<(options: OptionsCollab) => SessionCollab>,
  options: OptionsCollab,
): SessionCollab {
  let session: SessionCollab | null = null;
  let fermee = false;
  const chargement = ouvreur.then((ouvrir) => {
    if (fermee) return null;
    session = ouvrir(options);
    return session;
  });
  const pret = chargement.then((s) => (s === null ? Promise.reject(new Error('Session fermee.')) : s.pret));
  pret.catch(() => undefined);
  chargement.catch((e: unknown) => options.surErreur?.(e));
  return {
    pret,
    synchro: (): CollabSynchro => session?.synchro() ?? 'connexion',
    async fermer() {
      fermee = true;
      const s = await chargement.catch(() => null);
      if (s !== null) await s.fermer();
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Le client                                                                   */
/* -------------------------------------------------------------------------- */

const emulatorBoundFunctions = new WeakSet<Functions>();

/**
 * @param getActorId - uid de l'utilisateur connecte (`'system'` sans compte :
 *   rien n'est lu alors).
 * @param emulator - `config.emulators?.functions` en dev local, sinon omis.
 *   Storage est deja branche sur son emulateur par `initFirebase`.
 */
export function createNotesClient(
  db: Firestore,
  storage: FirebaseStorage,
  getActorId: () => string,
  orgId: string,
  app: FirebaseApp,
  emulator: EmulatorEndpoint | undefined,
): NotesClient {
  const col = (nom: string) => collection(db, 'orgs', orgId, nom);
  const uidConnecte = (): string | null => {
    const uid = getActorId();
    return uid === '' || uid === 'system' ? null : uid;
  };
  const cheminDeNoteEnBase = (noteId: string) => `orgs/${orgId}/${NOTES_COLLECTION}/${noteId}`;

  /** Les deux requetes permises (org, auteur), avec les memes contraintes. */
  function deuxRequetes(nom: string, uid: string, contraintes: QueryConstraint[]): Query[] {
    return [
      query(col(nom), where('portee', '==', 'org'), ...contraintes),
      query(col(nom), where('auteurUid', '==', uid), ...contraintes),
    ];
  }

  /**
   * Observe les deux requetes et rend leur fusion des que les DEUX ont
   * repondu : une demi-liste serait un etat faux.
   */
  function observerDeux<T extends { id: string }>(
    nom: string,
    contraintes: QueryConstraint[],
    rendre: (fusion: T[]) => void,
    comparer: (a: T, b: T) => number,
    erreur?: (e: unknown) => void,
  ): () => void {
    const uid = uidConnecte();
    if (uid === null) {
      rendre([]);
      return () => undefined;
    }
    const requetes = deuxRequetes(nom, uid, contraintes);
    const resultats: (T[] | null)[] = requetes.map(() => null);
    const arrets = requetes.map((q, i) =>
      onSnapshot(
        q,
        (snapshot) => {
          resultats[i] = snapshot.docs.map((d) => lireDocumentNote<T>(d.id, d.data()));
          if (resultats.every((r) => r !== null)) rendre(fusionnerParIdNotes(resultats, comparer));
        },
        (e) => erreur?.(e),
      ),
    );
    return () => arrets.forEach((arreter) => arreter());
  }

  /** La callable, fabriquee au premier appel. */
  let callable: ((donnees: Record<string, unknown>) => Promise<{ data: unknown }>) | null = null;
  function appelable(): (donnees: Record<string, unknown>) => Promise<{ data: unknown }> {
    if (callable !== null) return callable;
    const functions = getFunctions(app, NOTES_FUNCTIONS_REGION);
    if (emulator && !emulatorBoundFunctions.has(functions)) {
      connectFunctionsEmulator(functions, emulator.host, emulator.port);
      emulatorBoundFunctions.add(functions);
    }
    callable = httpsCallable<Record<string, unknown>, unknown>(functions, NOTES_CALLABLE);
    return callable;
  }

  async function appeler<T = unknown>(op: NotesOp, donnees: Record<string, unknown> = {}): Promise<T> {
    try {
      const { data } = await appelable()({ ...donnees, op });
      return data as T;
    } catch (e) {
      throw messageDErreurNotes(e);
    }
  }

  function uidExige(): string {
    const uid = uidConnecte();
    if (uid === null) throw new NotesError('Connexion requise.', 'unauthenticated');
    return uid;
  }

  return {
    observerListe(filtre, cb, erreur) {
      // Hors corbeille : triee par le serveur (index `portee|auteurUid,
      // deletedAt, archivedAt, modifieeAt desc`). Corbeille : `archivedAt !=
      // null`, une inegalite qui impose son propre ordre ; le tri se fait ici.
      const contraintes: QueryConstraint[] =
        filtre.corbeille === true
          ? [where('deletedAt', '==', null), where('archivedAt', '!=', null)]
          : [where('deletedAt', '==', null), where('archivedAt', '==', null), orderBy('modifieeAt', 'desc')];
      return observerDeux<Note>(NOTES_COLLECTION, contraintes, cb, parModificationDeNote, erreur);
    },

    observer(noteId, cb, erreur) {
      if (uidConnecte() === null || noteId === '') {
        cb(null);
        return () => undefined;
      }
      return onSnapshot(
        doc(db, 'orgs', orgId, NOTES_COLLECTION, noteId),
        (snapshot) => cb(snapshot.exists() ? lireDocumentNote<Note>(snapshot.id, snapshot.data()) : null),
        (e) => {
          // Une note qu'on ne peut pas lire (perso d'un autre, supprimee) se lit comme absente.
          if ((e as { code?: unknown }).code === 'permission-denied') cb(null);
          else erreur?.(e);
        },
      );
    },

    observerTags(cb, erreur) {
      if (uidConnecte() === null) {
        cb([]);
        return () => undefined;
      }
      return onSnapshot(
        col(NOTES_TAGS_COLLECTION),
        (snapshot) => cb(snapshot.docs.map((d) => lireDocumentNote<NoteTag>(d.id, d.data())).sort(parNom)),
        (e) => erreur?.(e),
      );
    },

    observerCollections(cb, erreur) {
      return observerDeux<NoteCollection>(NOTES_COLLECTIONS_COLLECTION, [], cb, parNom, erreur);
    },

    async chercher(saisie, limite) {
      const uid = uidConnecte();
      const terme = termeDeRecherche(saisie);
      if (uid === null || terme === null) return [];
      const n = limiteDeRecherche(limite);
      const requetes = deuxRequetes(NOTES_COLLECTION, uid, [
        where('recherche', 'array-contains', terme),
        where('deletedAt', '==', null),
        where('archivedAt', '==', null),
        orderBy('modifieeAt', 'desc'),
        limiter(n),
      ]);
      try {
        const reponses = await Promise.all(requetes.map((q) => getDocs(q)));
        const fusion = fusionnerParIdNotes(
          reponses.map((r) => r.docs.map((d) => lireDocumentNote<Note>(d.id, d.data()))),
          parModificationDeNote,
        );
        return filtrerRechercheNotes(fusion, saisie, n);
      } catch (e) {
        throw messageDErreurNotes(e);
      }
    },

    appeler,

    ouvrirContenu(noteId, options) {
      const uid = uidExige();
      return sessionDifferee(
        import('./collab.js').then(({ ouvrirCollab }) => (options2: OptionsCollab) => ouvrirCollab(db, options2)),
        {
          ...options,
          chemin: cheminDeNoteEnBase(noteId),
          uid,
          compacter: async () => {
            await appeler('compacter', { id: noteId });
          },
          lireStockage: async (chemin) => new Uint8Array(await getBytes(ref(storage, chemin))),
        },
      );
    },

    async ecrireMaj(noteId, update) {
      const uid = uidExige();
      try {
        const { ecrireMajCollab } = await import('./collab.js');
        await ecrireMajCollab(db, cheminDeNoteEnBase(noteId), update, uid);
      } catch (e) {
        throw messageDErreurNotes(e);
      }
    },

    blobs(noteId) {
      const dossier = `orgs/${orgId}/${NOTES_COLLECTION}/${noteId}/blobs`;
      return {
        async get(cle) {
          try {
            return await getBlob(ref(storage, cheminDeBlob(orgId, noteId, cle)));
          } catch (e) {
            if ((e as { code?: unknown }).code === 'storage/object-not-found') return null;
            throw messageDErreurNotes(e);
          }
        },
        async set(cle, blob) {
          if (blob.size > NOTES_BLOB_MAX_OCTETS) throw new NotesError('Fichier trop volumineux (50 Mo au plus).', 'invalid-argument');
          try {
            await uploadBytes(ref(storage, cheminDeBlob(orgId, noteId, cle)), blob, blob.type ? { contentType: blob.type } : undefined);
            return cle;
          } catch (e) {
            throw messageDErreurNotes(e);
          }
        },
        async delete(cle) {
          try {
            await deleteObject(ref(storage, cheminDeBlob(orgId, noteId, cle)));
          } catch (e) {
            if ((e as { code?: unknown }).code === 'storage/object-not-found') return;
            throw messageDErreurNotes(e);
          }
        },
        async list() {
          try {
            const { items } = await listAll(ref(storage, dossier));
            return items.map((item) => item.name);
          } catch (e) {
            throw messageDErreurNotes(e);
          }
        },
      };
    },
  };
}
